import { DrawerForm, DrawerSizeEnum } from '@acceso/design-system';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { HighlightContainer } from '@/components/FormFields/HighlightContainer';
import { InputDate } from '@/components/FormFields/InputDate';
import { InputFile } from '@/components/FormFields/InputFile';
import { InputNumber } from '@/components/FormFields/InputNumber';
import { InputText } from '@/components/FormFields/InputText';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { GIGABYTES_TO_BYTES } from '@/constants';
import { useChannelOptions } from '@/features/broadcasters/hooks/useChannelOptions';
import { useProductionCenterOptions } from '@/features/production-center/hooks/useProductionCenterOptions';
import { FormTypeEnum } from '@/types';
import { BaseDrawerFormProps } from '@/types/drawer-forms';

import { useChannelRecordingTaskTypeOptions } from '../../hooks/useChannelRecordingTaskTypeOptions';
import { useRecorderDrawerFormState } from '../../hooks/useRecorderDrawerFormState';
import {
  ChannelRecordingTaskFormData,
  ChannelRecordingTaskType,
  ChannelRecordingTaskTypeEnum,
} from '../../types';

type RecorderDrawerFormProps = BaseDrawerFormProps<
  ChannelRecordingTaskType,
  ChannelRecordingTaskFormData
>;

export const RecorderDrawerForm = ({
  data,
  isOpen,
  onClose,
  onSubmit,
  isMutating,
}: RecorderDrawerFormProps) => {
  const { t } = useTranslation();
  const { form } = useRecorderDrawerFormState();
  const productionCenterOptions = useProductionCenterOptions();
  const channelOptions = useChannelOptions({
    isParentChannel: false,
  });

  const sortedPoroductionCenterOptions = useMemo(() => {
    return productionCenterOptions.sort((a, b) => a.label.localeCompare(b.label));
  }, [productionCenterOptions]);

  const handleSubmit = form.handleSubmit(data => {
    onSubmit(data);
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen]);

  const channelRecordingTaskTypeOptions = useChannelRecordingTaskTypeOptions();
  const { recording_task_type: recordingTaskType } = useWatch({
    control: form.control,
  });
  const fileValue = useWatch({
    control: form.control,
    name: 'file',
  });
  const startDatetime = useWatch({
    control: form.control,
    name: 'start_datetime',
  });
  const startTime = useWatch({
    control: form.control,
    name: 'recording_start_time',
  });
  const endTime = useWatch({
    control: form.control,
    name: 'recording_end_time',
  });

  const [fileDuration, setFileDuration] = useState<number | null>(null);

  useEffect(() => {
    if (!fileValue) {
      setFileDuration(null);
      return;
    }

    const file = fileValue;
    const url = URL.createObjectURL(file);

    const media = document.createElement(file.type.startsWith('video') ? 'video' : 'audio');
    media.preload = 'metadata';
    media.src = url;

    media.onloadedmetadata = () => {
      setFileDuration(media.duration);
      URL.revokeObjectURL(url);
    };

    return () => {
      URL.revokeObjectURL(url);
      media.remove();
    };
  }, [fileValue]);

  useEffect(() => {
    if (fileDuration === null) {
      form.setValue('file_duration', '');
      return;
    }

    const parseTime = (time?: string) => {
      if (!time || time === '00:00:00') return null;
      const parts = time.split(':').map(Number);
      if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
      if (parts.length === 2) return parts[0] * 60 + parts[1];
      if (parts.length === 1) return parts[0];
      return null;
    };

    const start = parseTime(startTime);
    const end = parseTime(endTime);

    let duration = fileDuration;
    if (start !== null && end !== null) {
      duration = Math.max(0, end - start);
    } else if (start !== null) {
      duration = Math.max(0, fileDuration - start);
    } else if (end !== null) {
      duration = Math.max(0, end);
    }

    const h = Math.floor(duration / 3600)
      .toString()
      .padStart(2, '0');
    const m = Math.floor((duration % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const s = Math.floor(duration % 60)
      .toString()
      .padStart(2, '0');
    form.setValue('file_duration', `${h}:${m}:${s}`);

    if (recordingTaskType === ChannelRecordingTaskTypeEnum.FILE && startDatetime) {
      const startDate = new Date(startDatetime as unknown as Date);
      const endDate = new Date(startDate.getTime() + duration * 1000);
      form.setValue('end_datetime', endDate as unknown as string);
    }
  }, [recordingTaskType, startDatetime, fileDuration, startTime, endTime, form]);

  return (
    <DrawerForm
      title={
        !data?.data
          ? t('Crear grabación')
          : data?.type === FormTypeEnum.UPDATE
            ? 'Editar grabación'
            : 'Duplicar grabación'
      }
      isOpen={isOpen}
      onCancel={onClose}
      onSubmit={handleSubmit}
      size={DrawerSizeEnum.MD}
      isMutating={isMutating}
      labels={{
        submit: t('Guardar y ejecutar'),
        cancel: t('Cancelar'),
      }}
    >
      <InputText
        form={form}
        name="description"
        required
        label={t('Descripción')}
        placeholder="Escribe aqui la descripción"
      />

      <RowContainer>
        <SelectForm
          form={form}
          name="channel_id"
          required
          label={t('Emisora')}
          placeholder="Seleccionar..."
          options={channelOptions}
        />
        <SelectForm
          form={form}
          name="production_center_id"
          required
          label="Centro de producción"
          placeholder="Seleccionar..."
          options={sortedPoroductionCenterOptions}
        />
      </RowContainer>

      <SelectForm
        form={form}
        name="recording_task_type"
        required
        label={t('Tipo')}
        placeholder="Seleccionar..."
        options={channelRecordingTaskTypeOptions}
      />

      {recordingTaskType === ChannelRecordingTaskTypeEnum.FILE && (
        <HighlightContainer>
          <InputFile
            form={form}
            name="file"
            label={t('Seleccionar archivo')}
            required
            format={t('mp3 o mp4')}
            maxSize={1 * GIGABYTES_TO_BYTES}
            accept=".mp4, .mp3"
          />

          <RowContainer>
            <InputText
              form={form}
              name="recording_start_time"
              label={t('Punto de entrada')}
              placeholder="00:00:00"
            />

            <InputText
              form={form}
              name="recording_end_time"
              label={t('Punto de salida')}
              placeholder="00:00:00"
            />
          </RowContainer>
          <RowContainer>
            <InputText
              form={form}
              name="file_duration"
              label={t('Duración del fichero')}
              placeholder="00:00:00"
              disabled
            />
          </RowContainer>
        </HighlightContainer>
      )}
      {recordingTaskType === ChannelRecordingTaskTypeEnum.URL_STREAM && (
        <HighlightContainer>
          <InputText
            form={form}
            name="parameters.uri"
            label={t('URL stream')}
            placeholder="https://downloadtv-media-video01.com"
          />
        </HighlightContainer>
      )}
      {recordingTaskType === ChannelRecordingTaskTypeEnum.URL_FILE && (
        <HighlightContainer>
          <InputText
            form={form}
            name="parameters.uri"
            label={t('URL del fichero')}
            placeholder="https://downloadtv-media-video01.com/fichero.mp4"
          />

          <RowContainer>
            <InputText
              form={form}
              name="recording_start_time"
              label={t('Punto de entrada')}
              placeholder="00:00:00"
            />

            <InputText
              form={form}
              name="recording_end_time"
              label={t('Punto de salida')}
              placeholder="00:00:00"
            />
          </RowContainer>
        </HighlightContainer>
      )}

      <RowContainer>
        <InputDate
          form={form}
          name="start_datetime"
          label={t('Fecha y hora inicio')}
          required
          seconds
          showTimeField={true}
        />

        <InputDate
          form={form}
          name="end_datetime"
          label={t('Fecha y hora fin')}
          required
          seconds
          showTimeField={true}
        />

        <InputNumber
          form={form}
          name="segment_duration"
          label={t('Duración franja')}
          placeholder={t('{{count}} segundos', { count: 120 })}
          min={0}
          max={100000}
        />
      </RowContainer>
    </DrawerForm>
  );
};
